import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { calcPercent, formatNumber, formatPercent } from "../utils/percentage";
import { KpiKey } from "../utils/kpiFilterMap";

import { ClickableKpiRow } from "./ClickableKpiRow";

export interface KpiBarRowProps {
	label: string;
	value: number | undefined;
	total: number | undefined;
	/** When provided the row is clickable and triggers a drill-through. */
	clickKpi?: KpiKey;
	onKpiClick?: (kpi: KpiKey) => void;
	/** Override the bar's color (tailwind bg-*). Defaults to the brand blue. */
	barColorClass?: string;
	/** Hide the bar (used for small KPI tiles that only need the number). */
	hideBar?: boolean;
	/** Show the percentage as the primary value instead of the raw count. */
	emphasisPercent?: boolean;
	/** Treat the row as the baseline — always 100% bar, no percentage column. */
	isBaseline?: boolean;
}

const LABEL_WIDTH = "min-w-[160px] w-[160px]";
const PCT_PILL_WIDTH = "w-[72px]";
const VALUE_WIDTH = "w-[72px]";

export const KpiBarRow: FunctionComponent<KpiBarRowProps> = ({
	label,
	value,
	total,
	clickKpi,
	onKpiClick,
	barColorClass = "bg-blue-action-200",
	hideBar = false,
	emphasisPercent = false,
	isBaseline = false,
}) => {
	const { t } = useTranslation();
	const pct = isBaseline ? 100 : calcPercent(value, total);

	const content = (
		<>
			<div className={classNames("text-sm text-text-meta", LABEL_WIDTH)}>{label}</div>

			{!isBaseline && (
				<div
					className={classNames(
						"text-sm text-text-meta text-center px-2 py-1.5 border border-border-light rounded-xl tabular-nums",
						PCT_PILL_WIDTH
					)}
				>
					{formatPercent(pct)}
				</div>
			)}

			{isBaseline && (
				<div
					className={classNames(
						"text-sm text-text-meta text-center px-2 py-1.5 border border-border-light rounded-xl tabular-nums",
						PCT_PILL_WIDTH
					)}
				>
					100%
				</div>
			)}

			{!hideBar && (
				<div className="flex-1 h-3 bg-brand-white border border-border-light rounded-full overflow-hidden min-w-[120px]">
					<div
						className={classNames("h-full transition-all duration-300", barColorClass)}
						style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
					/>
				</div>
			)}

			<div
				className={classNames(
					"text-sm text-right tabular-nums",
					emphasisPercent ? "text-text font-semibold" : "text-text",
					VALUE_WIDTH
				)}
			>
				{emphasisPercent ? formatPercent(pct) : formatNumber(value)}
			</div>
		</>
	);

	if (clickKpi && onKpiClick) {
		return (
			<ClickableKpiRow
				ariaLabel={t("analytics.viewLeadsFor", { kpi: label })}
				onClick={() => onKpiClick(clickKpi)}
			>
				{content}
			</ClickableKpiRow>
		);
	}

	return <div className="flex flex-row items-center gap-4 -mx-2 px-2 py-1">{content}</div>;
};
